import React, { useState, createElement } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MarketplaceHeader } from '../components/layout/MarketplaceHeader';
import { Footer } from '../components/layout/Footer';
import { StatusTimeline } from '../components/tracking/StatusTimeline';
import { useOrderTracking } from '../lib/hooks';
import { Skeleton } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { formatCurrency, getStatusColor, getStatusLabel } from '../lib/utils';
import { Search, MapPin, Store, Receipt, Phone, Copy, CheckCircle2 } from 'lucide-react';
import { Badge } from '../components/ui/Badge';
export function TrackingPage() {
  const {
    trackingToken
  } = useParams<{
    trackingToken: string;
  }>();
  const {
    order,
    events,
    loading,
    error
  } = useOrderTracking(trackingToken);
  const [copied, setCopied] = useState(false);
  const handleCopyLink = async () => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(window.location.href);
      } else {
        // Safari / WhatsApp fallback using execCommand
        const textArea = document.createElement('textarea');
        textArea.value = window.location.href;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        textArea.style.top = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Silently fail if clipboard is blocked
      setCopied(false);
    }
  };
  if (loading) {
    return <div className="min-h-screen flex flex-col bg-brand-offwhite" data-id="element-2656">
        <MarketplaceHeader data-id="element-2657" />
        <main className="flex-1 max-w-3xl mx-auto w-full px-4 py-12" data-id="element-2658">
          <Skeleton className="h-12 w-1/2 mb-8" data-id="element-2659" />
          <div className="bg-white rounded-2xl p-6 shadow-sm mb-8" data-id="element-2660">
            <Skeleton className="h-8 w-1/3 mb-4" data-id="element-2661" />
            <Skeleton className="h-4 w-full mb-2" data-id="element-2662" />
            <Skeleton className="h-4 w-2/3" data-id="element-2663" />
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-sm" data-id="element-2664">
            {[1, 2, 3, 4].map(i => <div key={i} className="flex gap-4 mb-6" data-id="element-2665">
                <Skeleton className="h-10 w-10 rounded-full shrink-0" data-id="element-2666" />
                <div className="flex-1" data-id="element-2667">
                  <Skeleton className="h-6 w-1/3 mb-2" data-id="element-2668" />
                  <Skeleton className="h-4 w-full" data-id="element-2669" />
                </div>
              </div>)}
          </div>
        </main>
        <Footer data-id="element-2670" />
      </div>;
  }
  if (error || !order) {
    return <div className="min-h-screen flex flex-col bg-brand-offwhite" data-id="element-2671">
        <MarketplaceHeader data-id="element-2672" />
        <main className="flex-1 flex items-center justify-center py-12" data-id="element-2673">
          <EmptyState icon={Search} title="Pedido no encontrado" description="No pudimos encontrar un pedido con este enlace de seguimiento. Verifica que el enlace sea correcto." actionLabel="Volver al inicio" onAction={() => window.location.href = '/'} data-id="element-2674" />
        </main>
        <Footer data-id="element-2675" />
      </div>;
  }
  return <div className="min-h-screen flex flex-col bg-brand-offwhite" data-id="element-2676">
      <MarketplaceHeader data-id="element-2677" />

      <main className="flex-1 max-w-3xl mx-auto w-full px-4 sm:px-6 py-8 sm:py-12" data-id="element-2678">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8" data-id="element-2679">
          <div data-id="element-2680">
            <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-2681">
              Seguimiento de Pedido
            </h1>
            <div className="flex items-center gap-3" data-id="element-2682">
              <span className="text-gray-500 font-medium" data-id="element-2683">
                Orden #{order.order_number}
              </span>
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${getStatusColor(order.status)}`} data-id="element-2684">
                {getStatusLabel(order.status, order.restaurant_id)}
              </span>
            </div>
          </div>

          <button onClick={handleCopyLink} className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors shadow-sm w-fit" data-id="element-2685">
            {copied ? <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-2686" /> : <Copy className="w-4 h-4" data-id="element-2687" />}
            {copied ? 'Enlace copiado' : 'Compartir seguimiento'}
          </button>
        </div>

        {/* Order Info Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8" data-id="element-2688">
          <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-start gap-4" data-id="element-2689">
            <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center shrink-0" data-id="element-2690">
              <Store className="w-5 h-5 text-blue-600" data-id="element-2691" />
            </div>
            <div data-id="element-2692">
              <h3 className="text-sm font-semibold text-gray-500 mb-1" data-id="element-2693">
                Restaurante
              </h3>
              <p className="font-medium text-gray-900" data-id="element-2694">
                Pedido del restaurante
              </p>
              <p className="text-sm text-gray-500 mt-1" data-id="element-2695">
                {order.delivery_type === 'pickup' ? 'Para llevar' : order.delivery_type === 'delivery' ? 'Delivery' : 'Comer aquí'}
              </p>
            </div>
          </div>

          <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-start gap-4" data-id="element-2696">
            <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center shrink-0" data-id="element-2697">
              <Receipt className="w-5 h-5 text-emerald-600" data-id="element-2698" />
            </div>
            <div data-id="element-2699">
              <h3 className="text-sm font-semibold text-gray-500 mb-1" data-id="element-2700">
                Total a pagar
              </h3>
              <p className="font-bold text-xl text-gray-900" data-id="element-2701">
                {formatCurrency(order.total)}
              </p>
              <p className="text-sm text-gray-500 mt-1" data-id="element-2702">
                Pago:{' '}
                {order.payment_method === 'sinpe' ? 'SINPE Móvil' : order.payment_method === 'cash' ? 'Efectivo' : 'Tarjeta'}
              </p>
            </div>
          </div>
        </div>

        {/* Main Timeline */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 sm:p-8 mb-8" data-id="element-2703">
          <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-2704">
            Estado del Pedido
          </h2>
          <StatusTimeline currentStatus={order.status} events={events} restaurantId={order.restaurant_id} data-id="element-2705" />
        </div>

        {/* Delivery Info if applicable */}
        {order.delivery_type === 'delivery' && order.delivery_address && <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-8" data-id="element-2706">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-2707">
              <MapPin className="w-5 h-5 text-gray-400" data-id="element-2708" />
              Dirección de Entrega
            </h3>
            <p className="text-gray-600" data-id="element-2709">{order.delivery_address}</p>
            {order.notes && <div className="mt-4 p-3 bg-amber-50 rounded-xl text-sm text-amber-800 border border-amber-100" data-id="element-2710">
                <span className="font-semibold" data-id="element-2711">Notas:</span> {order.notes}
              </div>}
          </div>}

        <div className="text-center text-gray-500 text-sm" data-id="element-2712">
          <p data-id="element-2713">Esta página se actualiza automáticamente.</p>
          <p className="mt-2" data-id="element-2714">
            ¿Necesitas ayuda?{' '}
            <a href="#" className="text-brand-amber hover:underline font-medium" data-id="element-2715">
              Contacta al soporte
            </a>
          </p>
        </div>
      </main>

      <Footer data-id="element-2716" />
    </div>;
}